import {
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    ReferenceArea,
    Label,
} from "recharts";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { roundTo } from "@/lib/utils/numbers";
import { useMemo } from "react";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import dayjs from "dayjs";
import { GDD } from "@/types/field";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { DateValue, RangeValue, Spinner } from "@heroui/react";
import useRequest from "@/lib/request/useRequest";

const CHART_COLORS = {
    primary: "#09426A",
    secondary: "#74BD43",
    teal: "#00C4EB",
} as const;

const CHART_HEIGHT = 250;

export type GDDChartDatum = {
    date: Date;
    accumulated_gdd: number;
    [key: string]: unknown;
};

function GDDChart({ dateRange }: { dateRange: RangeValue<DateValue> | null }) {
    const { t } = useTranslation("insights");
    const { formatDateMMDDYYYY } = useI18n();
    const { useGetRequest } = useRequest();
    const { fieldId, selectedCropCycle } = useFieldDashboard() ?? {};

    const [startDate, endDate] = useMemo(() => {
        return [dayjs(dateRange?.start?.toString()), dayjs(dateRange?.end?.toString())];
    }, [dateRange]);

    const gddQuery = useGetRequest<GDD[]>({
        url: `/fields/${fieldId}/gdd/`,
        enabled: !!fieldId && !!selectedCropCycle,
    });

    const gddData = useMemo(() => {
        if (!gddQuery.data) return [];
        return gddQuery.data
            .filter((d) => {
                const date = dayjs(d.date);
                return (
                    date.isSameOrAfter(startDate, "day") &&
                    date.isSameOrBefore(dayjs(endDate))
                );
            })
            .map((row) => ({
                ...row,
                date: dayjs(row.date).toDate(),
            }));
    }, [gddQuery.data, endDate, startDate]);

    const forecastReferenceArea = useMemo(() => {
        const maxDate = dayjs.max(gddData.map((d) => dayjs(d.date)));
        if (!maxDate || !gddData.length) return null;

        return (
            <>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxDate,
                            getTodayWithTimeTravel().add(14, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke={CHART_COLORS.primary}
                    strokeOpacity={0.5}
                    fill={CHART_COLORS.primary}
                    fillOpacity={0.05}
                >
                    <Label
                        value="Forecasted"
                        position="top"
                        fontSize={10}
                        dx={-14}
                    />
                </ReferenceArea>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .add(14, "day")
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxDate,
                            getTodayWithTimeTravel().add(35, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke={CHART_COLORS.teal}
                    strokeOpacity={0.5}
                    fill={CHART_COLORS.teal}
                    fillOpacity={0.05}
                >
                    <Label value="Extended" position="top" fontSize={10} />
                </ReferenceArea>
                <ReferenceArea
                    x1={getTodayWithTimeTravel()
                        .add(35, "day")
                        .endOf("day")
                        .toDate()
                        .getTime()}
                    x2={dayjs
                        .min(
                            maxDate,
                            getTodayWithTimeTravel().add(215, "day"),
                        )
                        .toDate()
                        .getTime()}
                    stroke={CHART_COLORS.secondary}
                    strokeOpacity={0.5}
                    fill={CHART_COLORS.secondary}
                    fillOpacity={0.05}
                >
                    <Label value="Seasonal" position="top" fontSize={10} />
                </ReferenceArea>
            </>
        );
    }, [gddData]);

    return (
        <>
            <div className="font-semibold">
                {t("accumulatedGDD")}
            </div>
            {gddQuery.isLoading ? (
                <Spinner label={t("messages.loadingData")} />
            ) : !gddData.length ? (
                <div className="text-sm italic text-center mt-4">
                    {t("messages.noDataSelectedDates")}
                </div>
            ) : (<ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <LineChart
                    data={gddData}
                    margin={{ bottom: 16, left: 8 }}
                    syncId="weather"
                >
                    <XAxis
                        dataKey="date"
                        scale="time"
                        spacing={50}
                        angle={-8}
                        tickFormatter={(tick) => formatDateMMDDYYYY(tick)}
                        fontSize={12}
                    />
                    <YAxis
                        label={{
                            value: "Accumulated GDD",
                            angle: -90,
                            dx: -30,
                        }}
                        fontSize={12}
                    />
                    <Tooltip
                        labelFormatter={(label) => formatDateMMDDYYYY(label)}
                        formatter={(value) => [
                            `${roundTo(value as number, 1)}`,
                            t("accumulatedGDD"),
                        ]}
                    />

                    <Line
                        dataKey="accumulated_gdd"
                        stroke={CHART_COLORS.secondary}
                        fill={CHART_COLORS.primary}
                        dot={false}
                        strokeWidth={2}
                    />

                    {forecastReferenceArea}
                </LineChart>
            </ResponsiveContainer>)}
        </>
    );
}

export default GDDChart;
